/**
 * Device Movement Service
 * Service สำหรับจัดการ API การย้ายอุปกรณ์ข้ามโครงการ (device movements)
 *
 * Phase 1: ย้ายอุปกรณ์ทั้งหมดในสัญญาเช่าไปโครงการอื่นในบริษัทเดียวกัน
 * - Movement เป็น event ถาวรบันทึกหลังย้ายจริง (ไม่มี status/workflow)
 * - ไม่แตะสัญญา (contract) ใดๆ ใน phase นี้
 */
import ApiWithLogging from '../utils/apiWithLogging';

// ========== Types ==========

/** แถวผลค้นหา rental ที่ย้ายได้ (GET /search-rentals) */
export interface RentalPickerRow {
  rental_id: number;
  rental_number: string;
  status: string;
  company_id: number | null;
  company_name: string | null;
  site_id: number | null;
  site_name: string | null;
  item_count: number;
  devices_preview: string[];
}

export interface RentalSearchResponse {
  items: RentalPickerRow[];
  total: number;
  page: number;
  page_size: number;
}

/** รายการอุปกรณ์ใน transfer-context */
export interface TransferContextItem {
  rental_item_id: number;
  product_name: string | null;
  device_id: number | null;
  device_serial: string | null;
  device_name: string | null;
  sim_id: number | null;
  sim_number: string | null;
  is_software_only: boolean;
  /** Phase 3: item นี้เลือกย้ายบางรายการได้ (ทุก item ที่ยังมีชีวิตเลือกได้) */
  selectable: boolean;
  /** FB-2026-0016: ราคาเช่าปัจจุบัน (= "ราคาเดิม" บนฟอร์มปรับราคา · อ่านอย่างเดียว) — Decimal เป็น string */
  rental_rate?: string | number | null;
  /** FB-2026-0016: วันเริ่มราคาของการปรับครั้งล่าสุด — วันสิ้นสุดราคาเดิมรอบใหม่ต้องไม่ก่อนวันนี้ */
  last_price_change_start?: string | null;
}

/** FB-2026-0016: ปรับราคาเช่าของรายการที่ย้าย (ส่งได้เฉพาะผู้มีสิทธิ์ rentals.billing_manage) */
export interface PriceChangeRequest {
  rental_item_id: number;
  /** ราคาใหม่ต่อเดือน (> 0 · ทศนิยม 2 ตำแหน่ง) */
  new_rate: string;
  /** วันสุดท้ายที่คิดราคาเดิม (YYYY-MM-DD) */
  old_billing_end_date: string;
  /** วันแรกที่คิดราคาใหม่ — ต้องมากกว่าวันสิ้นสุดราคาเดิม (ห้ามช่วงซ้อน · เว้นวันได้) */
  new_billing_start_date: string;
  note?: string | null;
}

/** FB-2026-0016: การปรับราคาที่ทำพร้อมการย้าย (ในแถวประวัติ) */
export interface MovementPriceChangeRow {
  id: number;
  rental_item_id: number;
  device_name: string | null;
  device_serial: string | null;
  product_name: string | null;
  old_rate: string | number;
  new_rate: string | number;
  old_billing_end_date: string;
  new_billing_start_date: string;
  old_site_name: string | null;
  /** วันที่ไม่คิดเงินระหว่างสองช่วง (0 = ต่อเนื่อง) */
  gap_days: number;
  created_by_user_name: string | null;
}

/** ข้อมูลก่อนเปิดฟอร์มย้าย (GET /rentals/{id}/transfer-context) */
export interface TransferContext {
  rental: {
    id: number;
    rental_number: string;
    status: string;
    company_id: number;
    company_name: string | null;
    site_id: number | null;
    site_name: string | null;
    has_pending_cancellation_notice: boolean;
    cancellation_notice_id: number | null;
    /** FB-2026-0016: วันสุดท้ายของรอบที่ออกใบแจ้งหนี้แล้ว (ไม่นับ CANCELLED) — ราคาเดิมต้องจบไม่ก่อนวันนี้ */
    last_billed_period_end?: string | null;
  };
  items: TransferContextItem[];
  old_site: {
    id: number;
    name: string;
    status: string;
    /** rental อื่น (ไม่นับตัวนี้) ใน ACTIVE/PENDING_CANCELLATION/DRAFT บน site เดิม */
    remaining_active_rentals: number;
    /** contract ACTIVE บน site เดิม */
    active_contracts: number;
  };
  /** site บริษัทเดียวกัน ไม่รวม site ปัจจุบัน (รวม inactive — จะ auto-reactivate) */
  candidate_sites: Array<{ id: number; name: string; status: string }>;
}

/** body ของ POST /rentals/{id}/transfer */
export interface TransferRequest {
  to_site_id: number;
  reason: string;
  effective_date?: string;
  deactivate_old_site?: boolean;
  notes?: string | null;
  /**
   * Phase 3: เลือกย้ายบางรายการ (split rental)
   * ไม่ส่ง = ย้ายทั้งสัญญา · บางส่วน = แยกเป็นสัญญาใหม่ที่โครงการปลายทาง ·
   * ครบทุกรายการ = เท่ากับย้ายทั้งสัญญา (backend ไม่ split)
   */
  item_ids?: number[];
  /**
   * KD3 S2477: โครงการต้นทางที่ผู้ใช้เห็นตอนเปิดหน้าต่าง — backend ตอบ 409 ถ้ามีคนย้ายไปก่อนแล้ว
   * (กันการย้ายต่อจากโครงการที่ผู้ใช้ไม่เคยเห็น)
   */
  expected_from_site_id?: number | null;
  /**
   * FB-2026-0016: ปรับราคาเช่าพร้อมการย้าย — ต่อรายการที่ย้าย · ไม่มีสิทธิ์ rentals.billing_manage แล้วส่งมา = 403
   * (ไม่ส่ง = ย้ายได้ตามเดิม)
   */
  price_changes?: PriceChangeRequest[];
}

/** แถวประวัติการย้าย (GET / และ /history) */
export interface MovementRow {
  id: number;
  rental_id: number;
  rental_number: string;
  company_name: string | null;
  from_site_id: number | null;
  from_site_name: string;
  to_site_id: number | null;
  to_site_name: string;
  reason: string;
  effective_date: string;
  moved_by_user_name: string | null;
  item_count: number;
  notice_resolved: boolean;
  old_site_deactivated: boolean;
  is_partial: boolean;
  /** Phase 3: สัญญาใหม่ที่ split ออกไป (มีค่าเฉพาะ is_partial=true) */
  new_rental_id: number | null;
  new_rental_number: string | null;
  /** SPLIT-DEL-1: สัญญาใหม่ถูกลบไปแล้ว → แสดงเลขเป็น text ห้ามเป็นลิงก์ (เปิดแล้ว 404) */
  new_rental_is_deleted: boolean;
  /** FB-2026-0016: การปรับราคาเช่าที่ทำพร้อมการย้าย ([] = ไม่มี · API เก่าอาจไม่ส่ง) */
  price_changes?: MovementPriceChangeRow[];
  created_at: string;
}

export interface MovementListResponse {
  items: MovementRow[];
  total: number;
  page: number;
  page_size: number;
}

export interface MovementHistoryResponse {
  items: MovementRow[];
}

/** รายการอุปกรณ์ใน MovementDetail (snapshot) */
export interface MovementItemRow {
  device_id: number | null;
  device_serial: string | null;
  device_name: string | null;
  sim_id: number | null;
  sim_number: string | null;
  product_name: string | null;
}

export interface MovementDetail extends MovementRow {
  items: MovementItemRow[];
  notes: string | null;
}

/** response ของ POST /transfer = MovementDetail + warnings */
export interface TransferResponse extends MovementDetail {
  warnings: string[];
}

export interface MovementListFilters {
  page?: number;
  page_size?: number;
  company_id?: number;
  from_site_id?: number;
  to_site_id?: number;
  date_from?: string;
  date_to?: string;
  search?: string;
}

// ========== Service ==========

const deviceMovementService = {
  /**
   * ค้นหา rental ที่ย้ายได้ (สถานะ ACTIVE / PENDING_CANCELLATION)
   * ค้นจาก: rental_number, ชื่อโครงการ, ชื่อบริษัท, device SN
   */
  async searchRentals(
    query: string,
    options: { company_id?: number; site_id?: number; page?: number; page_size?: number } = {}
  ): Promise<RentalSearchResponse> {
    const params: Record<string, string | number | undefined> = {
      query,
      company_id: options.company_id,
      site_id: options.site_id,
      page: options.page || 1,
      page_size: options.page_size || 20,
    };
    const response = await ApiWithLogging.get<RentalSearchResponse>(
      '/device-movements/search-rentals',
      { params }
    );
    return response;
  },

  /**
   * ดึงข้อมูลก่อนเปิดฟอร์มย้าย (rental + รายการอุปกรณ์ + site ปลายทางที่เลือกได้)
   */
  async getTransferContext(rentalId: number): Promise<TransferContext> {
    const response = await ApiWithLogging.get<TransferContext>(
      `/device-movements/rentals/${rentalId}/transfer-context`
    );
    return response;
  },

  /**
   * ย้ายอุปกรณ์ทั้งหมดของ rental ไปโครงการปลายทาง (Phase 1: ย้ายทั้งหมดเสมอ)
   */
  async transferRental(rentalId: number, data: TransferRequest): Promise<TransferResponse> {
    const response = await ApiWithLogging.post<TransferResponse>(
      `/device-movements/rentals/${rentalId}/transfer`,
      data
    );
    return response;
  },

  /**
   * ดึงประวัติการย้ายทั้งระบบ (ตาราง event ตรงๆ)
   */
  async getMovements(filters: MovementListFilters = {}): Promise<MovementListResponse> {
    const params: Record<string, string | number | undefined> = {
      page: filters.page || 1,
      page_size: filters.page_size || 20,
    };
    if (filters.company_id) params.company_id = filters.company_id;
    if (filters.from_site_id) params.from_site_id = filters.from_site_id;
    if (filters.to_site_id) params.to_site_id = filters.to_site_id;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.search) params.search = filters.search;

    const response = await ApiWithLogging.get<MovementListResponse>(
      '/device-movements/',
      { params }
    );
    return response;
  },

  /**
   * ดึงรายละเอียดการย้าย 1 ครั้ง (พร้อมรายการอุปกรณ์ snapshot)
   */
  async getMovementDetail(movementId: number): Promise<MovementDetail> {
    const response = await ApiWithLogging.get<MovementDetail>(
      `/device-movements/${movementId}`
    );
    return response;
  },

  /**
   * ดึงประวัติการย้ายของ rental หนึ่งรายการ
   */
  async getRentalHistory(rentalId: number): Promise<MovementHistoryResponse> {
    const response = await ApiWithLogging.get<MovementHistoryResponse>(
      `/device-movements/rentals/${rentalId}/history`
    );
    return response;
  },

  /**
   * ดึงประวัติการย้ายของ device หนึ่งเครื่อง
   */
  async getDeviceHistory(deviceId: number): Promise<MovementHistoryResponse> {
    const response = await ApiWithLogging.get<MovementHistoryResponse>(
      `/device-movements/devices/${deviceId}/history`
    );
    return response;
  },

  /**
   * Export ประวัติการย้ายเป็น Excel (filter ชุดเดียวกับ list)
   */
  async exportToExcel(filters: Omit<MovementListFilters, 'page' | 'page_size'> = {}): Promise<Blob> {
    const params: Record<string, string | number | undefined> = {};
    if (filters.company_id) params.company_id = filters.company_id;
    if (filters.from_site_id) params.from_site_id = filters.from_site_id;
    if (filters.to_site_id) params.to_site_id = filters.to_site_id;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.search) params.search = filters.search;

    const response = await ApiWithLogging.get<Blob>(
      '/device-movements/export/excel',
      { params, responseType: 'blob' },
      {
        service: 'deviceMovementService',
        action: 'export_device_movements_excel',
        entityType: 'device_movement',
      }
    );
    return response;
  },
};

export default deviceMovementService;
